import { useRef, useState, useEffect, useCallback } from "react";
import { Checkbox, Panel, DefaultButton, TextField, SpinButton, Slider, MessageBar, MessageBarType } from "@fluentui/react";
import { SparkleFilled, WindowHeaderVerticalRegular, ChevronDown20Filled } from "@fluentui/react-icons";
import readNDJSONStream from "ndjson-readablestream";
import { getHeaders } from "../../api";

import logo from "../../assets/gpt-image.png";
import undplogo from "../../assets/undp-logo-blue.png";
import styles from "./Chat.module.css";

import uuid from "react-uuid";

import {
    chatApi,
    chatStreamApi,
    chatCancelApi,
    configApi,
    RetrievalMode,
    ChatAppResponseOrError,
    ChatAppRequest,
    BotAppResponse,
    ResponseMessage,
    VectorFieldOptions,
    GPT4VInput,
    StreamingChunk
} from "../../api";
import { AnswerBot, AnswerError, AnswerLoading } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { ExampleList } from "../../components/ExampleCustom";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { HistoryPanel } from "../../components/HistoryPanel";
import { HistoryProviderOptions, useHistoryManager } from "../../components/HistoryProviders";
import { SettingsButton } from "../../components/SettingsButton";
import { ClearChatButton } from "../../components/ClearChatButton";
import { UploadFile } from "../../components/UploadFile";
import { useLogin, getToken, isLoggedIn, requireAccessControl, getLocalAccountId } from "../../authConfig";
import { VectorSettings } from "../../components/VectorSettings";
import { useMsal } from "@azure/msal-react";
import { TokenClaimsDisplay } from "../../components/TokenClaimsDisplay";
import { GPT4VSettings } from "../../components/GPT4VSettings";
import { RadioGroup, Radio, Field, Tooltip } from "@fluentui/react-components";
import { Brush, Scale, FileText, X } from "lucide-react";

import { HistorySidebar } from "../../components/HistorySidebar";
import { useNavigate } from "react-router-dom";
import Progress from "../../components/Progress/Progress";

const Chat = () => {
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(true);
    const [promptTemplate, setPromptTemplate] = useState<string>("");
    const [temperature, setTemperature] = useState<number>(0.3);
    const [minimumRerankerScore, setMinimumRerankerScore] = useState<number>(0);
    const [minimumSearchScore, setMinimumSearchScore] = useState<number>(0);
    const [retrieveCount, setRetrieveCount] = useState<number>(3);
    const [retrievalMode, setRetrievalMode] = useState<RetrievalMode>(RetrievalMode.Hybrid);
    const [useSemanticRanker, setUseSemanticRanker] = useState<boolean>(true);
    const [shouldStream, setShouldStream] = useState<boolean>(true);
    const [useSemanticCaptions, setUseSemanticCaptions] = useState<boolean>(false);
    const [excludeCategory, setExcludeCategory] = useState<string>("");
    const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(false);
    const [vectorFieldList, setVectorFieldList] = useState<VectorFieldOptions[]>([VectorFieldOptions.Embedding]);
    const [useOidSecurityFilter, setUseOidSecurityFilter] = useState<boolean>(false);
    const [useGroupsSecurityFilter, setUseGroupsSecurityFilter] = useState<boolean>(false);
    const [gpt4vInput, setGPT4VInput] = useState<GPT4VInput>(GPT4VInput.TextAndImages);
    const [useGPT4V, setUseGPT4V] = useState<boolean>(false);
    const [conversationStyle, setConversationStyle] = useState("creative");
    const [chat_id, setChatID] = useState<string>(uuid().toString());

    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const chatContainerRef = useRef<HTMLDivElement | null>(null);
    const [showScrollToBottom, setShowScrollToBottom] = useState<boolean>(false);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();
    const [isStreamingOutputSuppressed, setIsStreamingOutputSuppressed] = useState<boolean>(false);
    const isStreamingOutputSuppressedRef = useRef<boolean>(false);
    const skipTypewriterForThisResponseRef = useRef<boolean>(false);
    const [skipTypewriterAnswerIndices, setSkipTypewriterAnswerIndices] = useState<Set<number>>(new Set());
    const [isLastAnswerTypewriting, setIsLastAnswerTypewriting] = useState<boolean>(false);
    const streamingAbortControllerRef = useRef<AbortController | null>(null);

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);

    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    const [answers, setAnswers] = useState<[user: string, response: BotAppResponse][]>([]);
    const [streamedAnswers, setStreamedAnswers] = useState<[user: string, response: BotAppResponse][]>([]);
    const [currentStreamingResponse, setCurrentStreamingResponse] = useState<string>("");
    const [streamingStatus, setStreamingStatus] = useState<string>("");
    const [activeTools, setActiveTools] = useState<string[]>([]);
    const [streamingSteps, setStreamingSteps] = useState<{ type: string; content: string; timestamp: number }[]>([]);
    const [completedStreamingSteps, setCompletedStreamingSteps] = useState<{ type: string; content: string; timestamp: number }[]>([]);
    const [answerStreamingSteps, setAnswerStreamingSteps] = useState<Map<number, { type: string; content: string; timestamp: number }[]>>(new Map());
    const [expandedThinking, setExpandedThinking] = useState<Set<number>>(new Set());
    const [thinkingStartTime, setThinkingStartTime] = useState<number>(0);
    const [totalThinkingTime, setTotalThinkingTime] = useState<Map<number, number>>(new Map());
    const [showGPT4VOptions, setShowGPT4VOptions] = useState<boolean>(false);
    const [showSemanticRankerOption, setShowSemanticRankerOption] = useState<boolean>(false);
    const [showVectorOption, setShowVectorOption] = useState<boolean>(false);
    const [showUserUpload, setShowUserUpload] = useState<boolean>(false);
    const [showChatHistoryBrowser, setShowChatHistoryBrowser] = useState<boolean>(false);
    const [showChatHistoryCosmos, setShowChatHistoryCosmos] = useState<boolean>(false);
    const [showChatHistoryPostgres, setShowChatHistoryPostgres] = useState<boolean>(true);

    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();

    const navigate = useNavigate();

    const getConfig = async () => {
        const config = configApi();

        setShowGPT4VOptions(config.showGPT4VOptions);
        setUseSemanticRanker(config.showSemanticRankerOption);
        setShowSemanticRankerOption(config.showSemanticRankerOption);
        setShowVectorOption(config.showVectorOption);
        if (!config.showVectorOption) {
            setRetrievalMode(RetrievalMode.Text);
        }
        setShowUserUpload(config.showUserUpload);
        setShowChatHistoryBrowser(config.showChatHistoryBrowser);
        setShowChatHistoryCosmos(config.showChatHistoryPostgres);
    };

    const client = useLogin ? useMsal().instance : undefined;

    const historyProvider: HistoryProviderOptions = (() => {
        if (showChatHistoryPostgres) return HistoryProviderOptions.Postgres;
        if (showChatHistoryBrowser) return HistoryProviderOptions.IndexedDB;
        return HistoryProviderOptions.None;
    })();

    const historyManager = useHistoryManager(historyProvider);

    const formatElapsedSeconds = (seconds?: number, decimals = 0) => {
        if (!Number.isFinite(seconds)) return "0";
        return Math.max(0, seconds as number).toFixed(decimals);
    };

    const formatElapsedMs = (ms?: number, decimals = 1) => {
        if (!Number.isFinite(ms)) return "0.0s";
        return `${(Math.max(0, ms as number) / 1000).toFixed(decimals)}s`;
    };

    const makeApiRequest = async (question: string, selectedFile: File | null) => {
        lastQuestionRef.current = question;

        error && setError(undefined);
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);

        const token = client ? await getToken(client) : undefined;

        if (selectedFile) {
            const reader = new FileReader();

            reader.onload = async () => {
                let content = reader.result;

                if (
                    selectedFile.type === "application/pdf" ||
                    selectedFile.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
                    selectedFile.type === "application/vnd.openxmlformats-officedocument.presentationml.presentation"
                ) {
                    // For PDFs and DOCX files, encode the binary content to base64
                    content = btoa(new Uint8Array(content as ArrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), ""));
                }

                const payload = {
                    question: question,
                    fileName: selectedFile.name,
                    fileType: selectedFile.type,
                    fileContent: content
                };

                try {
                    // Send only the latest user message - backend loads history from DB
                    const request: ChatAppRequest = {
                        messages: [{ content: question, role: "user" }],
                        chat_id: chat_id,
                        conversation_style: conversationStyle,
                        fileName: selectedFile.name,
                        fileType: selectedFile.type,
                        file: content
                    };

                    const response = await chatApi(request, token);
                    if (!response.body) {
                        throw Error("No response body");
                    }

                    const parsedResponse: ChatAppResponseOrError = await response.json();
                    if (response.status > 299 || !response.ok) {
                        throw Error(parsedResponse.error || "Unknown error");
                    }
                    setAnswers([...answers, [question, parsedResponse as BotAppResponse]]);

                    // if (typeof parsedResponse.session_state === "string" && parsedResponse.session_state !== "") {
                    //     const token = client ? await getToken(client) : "123456";
                    //     historyManager.addItem(
                    //         parsedResponse.session_state,
                    //         [...answers, [question, parsedResponse as BotAppResponse]],
                    //         activeAccount?.localAccountId,
                    //         token
                    //     );
                    // }
                } catch (e) {
                    setError(e);
                } finally {
                    setIsLoading(false);
                }
            };

            if (
                selectedFile.type === "application/pdf" ||
                selectedFile.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
                selectedFile.type === "application/vnd.openxmlformats-officedocument.presentationml.presentation"
            ) {
                reader.readAsArrayBuffer(selectedFile);
            } else {
                alert("Unsupported file type");
            }
        } else {
            try {
                // Send only the latest user message - backend loads history from DB
                const request: ChatAppRequest = {
                    messages: [{ content: question, role: "user" }],
                    session_state: answers.length ? answers[answers.length - 1][1].session_state : null,
                    chat_id: chat_id,
                    conversation_style: conversationStyle
                };

                const response = await chatApi(request, token);
                if (!response.body) {
                    throw Error("No response body");
                }

                const parsedResponse: ChatAppResponseOrError = await response.json();
                if (response.status > 299 || !response.ok) {
                    throw Error(parsedResponse.error || "Unknown error");
                }
                setAnswers([...answers, [question, parsedResponse as BotAppResponse]]);
                // if (typeof parsedResponse.session_state === "string" && parsedResponse.session_state !== "") {
                //     const token = client ? await getToken(client) : undefined;
                //     historyManager.addItem(
                //         parsedResponse.session_state,
                //         [...answers, [question, parsedResponse as BotAppResponse]],
                //         activeAccount?.localAccountId,
                //         token
                //     );
                // }
            } catch (e) {
                setError(e);
            } finally {
                setIsLoading(false);
            }
        }
    };

    // const makeStreamingApiRequest = async (question: string, selectedFile: File | null) => {
    //     lastQuestionRef.current = question;

    //     error && setError(undefined);
    //     setIsLoading(true);
    //     setIsStreaming(true);
    //     setActiveCitation(undefined);
    //     setActiveAnalysisPanelTab(undefined);
    //     setCurrentStreamingResponse("");
    //     setStreamingStatus("");
    //     setActiveTools([]);
    //     setStreamingSteps([]);
    //     setThinkingStartTime(Date.now());

    //     // Add initial step immediately
    //     const initialStep = {
    //         type: "thinking",
    //         content: "Question sent to agent",
    //         timestamp: 0
    //     };
    //     setStreamingSteps([initialStep]);

    //     const token = client ? await getToken(client) : undefined;

    //     try {
    //         const messages: ResponseMessage[] = answers.flatMap(a => [
    //             { content: a[0], role: "user" },
    //             { content: a[1].message, role: "assistant" }
    //         ]);

    //         const request: ChatAppRequest = {
    //             messages: [...messages, { content: question, role: "user" }],
    //             session_state: answers.length ? answers[answers.length - 1][1].session_state : null,
    //             chat_id: chat_id,
    //             conversation_style: conversationStyle,
    //             fileName: selectedFile?.name,
    //             fileType: selectedFile?.type,
    //             file: selectedFile ? await fileToBase64(selectedFile) : null
    //         };

    //         const response = await chatStreamApi(request, token);
    //         if (!response.body) {
    //             throw Error("No response body");
    //         }

    //         const reader = response.body.getReader();
    //         const decoder = new TextDecoder();

    //         let accumulatedContent = "";
    //         let finalFollowupQuestions: string[] = [];
    //         let localStreamingSteps: { type: string; content: string; timestamp: number }[] = [initialStep];
    //         const startTime = Date.now();

    //         while (true) {
    //             const { done, value } = await reader.read();
    //             if (done) break;

    //             const chunk = decoder.decode(value);
    //             const lines = chunk.split("\n");

    //             for (const line of lines) {
    //                 if (line.startsWith("data: ")) {
    //                     try {
    //                         const jsonData = line.slice(6).trim();
    //                         if (jsonData) {
    //                             const parsedChunk: StreamingChunk = JSON.parse(jsonData);
    //                             const timestamp = Date.now() - startTime;

    //                             console.log("Received streaming chunk:", parsedChunk);

    //                             if (parsedChunk.type === "thinking") {
    //                                 setStreamingStatus(parsedChunk.content || "Analyzing your question...");
    //                                 const stepData = {
    //                                     type: "thinking",
    //                                     content: parsedChunk.content || "Analyzing your question...",
    //                                     timestamp
    //                                 };
    //                                 localStreamingSteps.push(stepData);
    //                                 setStreamingSteps(prev => [...prev, stepData]);
    //                             } else if (parsedChunk.type === "tool_call") {
    //                                 const toolName = parsedChunk.tool_name || "Tool";
    //                                 setStreamingStatus(parsedChunk.content || `Searching for information...`);
    //                                 setActiveTools(prev => {
    //                                     if (!prev.includes(toolName)) {
    //                                         return [...prev, toolName];
    //                                     }
    //                                     return prev;
    //                                 });
    //                                 const stepData = {
    //                                     type: "tool_call",
    //                                     content: `${toolName}: ${parsedChunk.content || "Searching..."}`,
    //                                     timestamp
    //                                 };
    //                                 localStreamingSteps.push(stepData);
    //                                 setStreamingSteps(prev => [...prev, stepData]);
    //                             } else if (parsedChunk.type === "content_delta") {
    //                                 // Start showing content generation
    //                                 setStreamingStatus("Writing response...");
    //                                 const delta = parsedChunk.content || "";
    //                                 accumulatedContent += delta;
    //                                 setCurrentStreamingResponse(accumulatedContent);
    //                             } else if (parsedChunk.type === "final_response") {
    //                                 finalFollowupQuestions = parsedChunk.followup_questions || [];
    //                                 setCurrentStreamingResponse(parsedChunk.content || accumulatedContent);
    //                                 accumulatedContent = parsedChunk.content || accumulatedContent;
    //                                 setStreamingStatus("");
    //                                 const stepData = {
    //                                     type: "completion",
    //                                     content: "Response completed",
    //                                     timestamp
    //                                 };
    //                                 localStreamingSteps.push(stepData);
    //                                 setStreamingSteps(prev => [...prev, stepData]);
    //                             } else if (parsedChunk.type === "error") {
    //                                 setStreamingStatus("❌ Error occurred");
    //                                 throw new Error(parsedChunk.content || "Unknown streaming error");
    //                             } else if (parsedChunk.type === "stream_end") {
    //                                 // Streaming complete
    //                                 setStreamingStatus("");
    //                                 break;
    //                             }
    //                         }
    //                     } catch (parseError) {
    //                         console.warn("Failed to parse chunk:", parseError);
    //                     }
    //                 }
    //             }
    //         }

    //         // Add the final response to answers
    //         const finalResponse: BotAppResponse = {
    //             message: accumulatedContent || currentStreamingResponse,
    //             session_state: chat_id, // Use chat_id as session state for now
    //             followup_questions: finalFollowupQuestions
    //         };

    //         // Preserve the thinking steps for the completed response BEFORE clearing
    //         setCompletedStreamingSteps([...localStreamingSteps]);

    //         // Store steps for this specific answer
    //         const newAnswerIndex = answers.length;
    //         const thinkingDuration = (Date.now() - startTime) / 1000; // Convert to seconds
    //         console.log("Time calculation debug:", {
    //             currentTime: Date.now(),
    //             startTime,
    //             difference: Date.now() - startTime,
    //             thinkingDuration
    //         });

    //         setAnswerStreamingSteps(prev => {
    //             const newMap = new Map(prev);
    //             newMap.set(newAnswerIndex, [...localStreamingSteps]);
    //             console.log("AFTER storing - newMap for index", newAnswerIndex, ":", newMap.get(newAnswerIndex));
    //             return newMap;
    //         });

    //         setTotalThinkingTime(prev => {
    //             const newMap = new Map(prev);
    //             newMap.set(newAnswerIndex, thinkingDuration);
    //             return newMap;
    //         });

    //         setAnswers([...answers, [question, finalResponse]]);

    //         setCurrentStreamingResponse("");
    //         setStreamingStatus("");
    //         setActiveTools([]);
    //         setStreamingSteps([]);
    //     } catch (e) {
    //         setError(e);
    //         setStreamingStatus("❌ Error occurred");
    //     } finally {
    //         setIsLoading(false);
    //         setIsStreaming(false);
    //     }
    // };

    const makeStreamingApiRequest = async (question: string, selectedFile: File | null) => {
        lastQuestionRef.current = question;

        if (error) setError(undefined);
        setIsLoading(true);
        setIsStreaming(true);
        setIsStreamingOutputSuppressed(false);
        isStreamingOutputSuppressedRef.current = false;
        skipTypewriterForThisResponseRef.current = false;
        setIsLastAnswerTypewriting(false);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setCurrentStreamingResponse("");
        setStreamingStatus("");
        setActiveTools([]);
        setStreamingSteps([]);
        const requestStartTime = Date.now();
        setThinkingStartTime(requestStartTime);

        // Add initial step immediately
        const initialStep = {
            type: "thinking",
            content: "Question sent to agent",
            timestamp: 0
        };
        setStreamingSteps([initialStep]);

        const token = client ? await getToken(client) : undefined;

        try {
            // Cancel any previous in-flight stream (shouldn't normally happen, but keeps state sane)
            streamingAbortControllerRef.current?.abort();
            const abortController = new AbortController();
            streamingAbortControllerRef.current = abortController;

            // Send only the latest user message - backend loads history from DB
            const request: ChatAppRequest = {
                messages: [{ content: question, role: "user" }],
                session_state: answers.length ? answers[answers.length - 1][1].session_state : null,
                chat_id: chat_id,
                conversation_style: conversationStyle,
                fileName: selectedFile?.name,
                fileType: selectedFile?.type,
                file: selectedFile ? await fileToBase64(selectedFile) : null
            };

            const response = await chatStreamApi(request, token, abortController.signal);
            if (!response.body) {
                throw Error("No response body");
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            let accumulatedContent = "";
            let finalFollowupQuestions: string[] = [];
            let localStreamingSteps: { type: string; content: string; timestamp: number }[] = [initialStep];
            const startTime = requestStartTime;

            // ---- NEW: Robust SSE parsing state ----
            let textRemainder = ""; // holds partial line across chunks
            let eventDataLines: string[] = []; // accumulates "data:" lines for one SSE event
            let stop = false; // set when we see [DONE] or stream_end

            const dispatchEvent = (payload: string) => {
                // Per convention, servers may send "[DONE]" as a terminal sentinel
                if (!payload || payload === "[DONE]") {
                    stop = true;
                    return;
                }
                try {
                    const parsedChunk: StreamingChunk = JSON.parse(payload);
                    const timestamp = Date.now() - startTime;

                    console.log("Received streaming chunk:", parsedChunk);

                    switch (parsedChunk.type) {
                        case "thinking": {
                            setStreamingStatus(parsedChunk.content || "Analyzing your question...");
                            const stepData = {
                                type: "thinking",
                                content: parsedChunk.content || "Analyzing your question...",
                                timestamp
                            };
                            localStreamingSteps.push(stepData);
                            setStreamingSteps(prev => [...prev, stepData]);
                            break;
                        }
                        case "tool_call": {
                            const toolName = parsedChunk.tool_name || "Tool";
                            setStreamingStatus(parsedChunk.content || "Searching for information...");
                            setActiveTools(prev => (prev.includes(toolName) ? prev : [...prev, toolName]));
                            const stepData = {
                                type: "tool_call",
                                content: `${toolName}: ${parsedChunk.content || "Searching..."}`,
                                timestamp
                            };
                            localStreamingSteps.push(stepData);
                            setStreamingSteps(prev => [...prev, stepData]);
                            break;
                        }
                        case "content_delta": {
                            const delta = parsedChunk.content || "";
                            accumulatedContent += delta;
                            if (!isStreamingOutputSuppressedRef.current) {
                                setStreamingStatus("Writing response...");
                                setCurrentStreamingResponse(accumulatedContent);
                            }
                            break;
                        }
                        case "final_response": {
                            finalFollowupQuestions = parsedChunk.followup_questions || [];
                            // Always show final content (even if we suppressed incremental deltas)
                            setCurrentStreamingResponse(parsedChunk.content || accumulatedContent);
                            accumulatedContent = parsedChunk.content || accumulatedContent;
                            setStreamingStatus("");
                            const stepData = { type: "completion", content: "Response completed", timestamp };
                            localStreamingSteps.push(stepData);
                            setStreamingSteps(prev => [...prev, stepData]);
                            break;
                        }
                        case "error": {
                            setStreamingStatus("❌ Error occurred");
                            throw new Error(parsedChunk.content || "Unknown streaming error");
                        }
                        case "stream_end": {
                            setStreamingStatus("");
                            stop = true;
                            break;
                        }
                        default:
                            // ignore unknown types
                            break;
                    }
                } catch (e) {
                    console.warn("Failed to parse chunk:", e, {
                        preview: payload.slice(0, 300),
                        len: payload.length
                    });
                }
            };

            while (!stop) {
                const { done, value } = await reader.read();

                if (done) {
                    // Flush any internal decoder state and allow the last event to flush
                    textRemainder += decoder.decode();
                } else {
                    // Important: stream=true so decoder can handle UTF-8 characters across chunk boundaries
                    textRemainder += decoder.decode(value, { stream: true });
                }

                // Split into complete lines; keep the last (possibly partial) line
                const lines = textRemainder.split(/\r?\n/);
                textRemainder = lines.pop() ?? "";

                for (const rawLine of lines) {
                    const line = rawLine; // keep as-is; we only slice after "data:"

                    if (line === "") {
                        // Blank line = end of one SSE event → join and dispatch
                        const payload = eventDataLines.join("\n");
                        eventDataLines = [];
                        dispatchEvent(payload);
                        if (stop) break;
                        continue;
                    }

                    if (line.startsWith(":")) {
                        // comment / heartbeat → ignore
                        continue;
                    }

                    if (line.startsWith("data:")) {
                        // Keep leading spaces after "data:" per spec
                        eventDataLines.push(line.slice(5).trimStart());
                        continue;
                    }

                    // Optional: handle "event:", "id:", "retry:" if your server uses them
                }

                if (done) {
                    // If stream ended without a trailing blank line, flush the final event
                    if (eventDataLines.length > 0) {
                        const payload = eventDataLines.join("\n");
                        eventDataLines = [];
                        dispatchEvent(payload);
                    }
                    break;
                }
            }

            // Add the final response to answers
            const finalResponse: BotAppResponse = {
                message: accumulatedContent || currentStreamingResponse,
                session_state: chat_id, // Use chat_id as session state for now
                followup_questions: finalFollowupQuestions
            };

            // Preserve the thinking steps for the completed response BEFORE clearing
            setCompletedStreamingSteps([...localStreamingSteps]);

            // Store steps for this specific answer
            const newAnswerIndex = answers.length;
            const thinkingDuration = (Date.now() - startTime) / 1000; // seconds

            setAnswerStreamingSteps(prev => {
                const newMap = new Map(prev);
                newMap.set(newAnswerIndex, [...localStreamingSteps]);
                return newMap;
            });

            setTotalThinkingTime(prev => {
                const newMap = new Map(prev);
                newMap.set(newAnswerIndex, thinkingDuration);
                return newMap;
            });

            if (skipTypewriterForThisResponseRef.current) {
                setSkipTypewriterAnswerIndices(prev => {
                    const next = new Set(prev);
                    next.add(newAnswerIndex);
                    return next;
                });
            }

            setAnswers([...answers, [question, finalResponse]]);

            setCurrentStreamingResponse("");
            setStreamingStatus("");
            setActiveTools([]);
            setStreamingSteps([]);
        } catch (e: any) {
            // Treat user abort as a normal stop (no error banner)
            if (e?.name === "AbortError") {
                setStreamingStatus("");
                setCurrentStreamingResponse("");
                setActiveTools([]);
                setStreamingSteps([]);
                return;
            }

            setError(e);
            setStreamingStatus("❌ Error occurred");
        } finally {
            streamingAbortControllerRef.current = null;
            setIsLoading(false);
            setIsStreaming(false);
        }
    };

    // Helper function to convert file to base64
    const fileToBase64 = (file: File): Promise<string | ArrayBuffer> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                if (
                    file.type === "application/pdf" ||
                    file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
                    file.type === "application/vnd.openxmlformats-officedocument.presentationml.presentation"
                ) {
                    // For PDFs and DOCX files, encode the binary content to base64
                    const content = btoa(new Uint8Array(reader.result as ArrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), ""));
                    resolve(content);
                } else {
                    resolve(reader.result as string);
                }
            };
            reader.onerror = reject;

            if (
                file.type === "application/pdf" ||
                file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
                file.type === "application/vnd.openxmlformats-officedocument.presentationml.presentation"
            ) {
                reader.readAsArrayBuffer(file);
            } else {
                reader.readAsText(file);
            }
        });
    };

    const clearChat = () => {
        lastQuestionRef.current = "";
        streamingAbortControllerRef.current?.abort();
        streamingAbortControllerRef.current = null;
        error && setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        setStreamedAnswers([]);
        setCurrentStreamingResponse("");
        setStreamingStatus("");
        setActiveTools([]);
        setStreamingSteps([]);
        setCompletedStreamingSteps([]);
        setAnswerStreamingSteps(new Map());
        setExpandedThinking(new Set());
        setTotalThinkingTime(new Map());
        setThinkingStartTime(0);
        setIsStreamingOutputSuppressed(false);
        isStreamingOutputSuppressedRef.current = false;
        skipTypewriterForThisResponseRef.current = false;
        setSkipTypewriterAnswerIndices(new Set());
        setIsLastAnswerTypewriting(false);
        setIsLoading(false);
        setIsStreaming(false);
        setChatID(uuid().toString()); // Generate a new chat ID for the new chat
    };

    const handleStop = async () => {
        // Case 1: we're currently streaming → stop incremental UI updates but keep the request running
        if (isStreaming && !isStreamingOutputSuppressedRef.current) {
            setIsStreamingOutputSuppressed(true);
            isStreamingOutputSuppressedRef.current = true;
            skipTypewriterForThisResponseRef.current = true;
            setStreamingStatus("");
            return;
        }

        // Case 1b: already suppressed but still running → abort the request on the agent/backend
        if (isStreaming && isStreamingOutputSuppressedRef.current) {
            const token = client ? await getToken(client) : undefined;
            try {
                await chatCancelApi(chat_id, token);
            } catch {
                // ignore (we still abort the fetch to stop the client-side stream)
            }
            streamingAbortControllerRef.current?.abort();
            setIsStreamingOutputSuppressed(false);
            isStreamingOutputSuppressedRef.current = false;
            skipTypewriterForThisResponseRef.current = true;
            setStreamingStatus("");
            return;
        }

        // Case 2: last completed answer is typewriting → skip its typewriter effect immediately
        if (isLastAnswerTypewriting && answers.length > 0) {
            const lastIndex = answers.length - 1;
            setSkipTypewriterAnswerIndices(prev => {
                const next = new Set(prev);
                next.add(lastIndex);
                return next;
            });
            setIsLastAnswerTypewriting(false);
        }
    };

    useEffect(() => chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" }), [isLoading]);
    useEffect(() => chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" }), [streamedAnswers]);
    useEffect(() => {
        getConfig();
    }, []);

    // Scroll to bottom functionality
    const scrollToBottom = () => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    };
    const scrollToBottomInstant = () => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" });
    };

    const handleScroll = useCallback(() => {
        // Check both window scroll and container scroll
        const container = chatContainerRef.current;
        if (!container) return;

        // Find the scrollable parent element (look for element with overflow-y: scroll/auto)
        let scrollableElement: HTMLElement | Window = window;
        let parent: HTMLElement | null = container.parentElement;

        while (parent) {
            const style = window.getComputedStyle(parent);
            if (style.overflowY === "scroll" || style.overflowY === "auto") {
                scrollableElement = parent;
                break;
            }
            parent = parent.parentElement;
        }

        let scrollTop: number;
        let scrollHeight: number;
        let clientHeight: number;

        if (scrollableElement === window) {
            scrollTop = window.scrollY || document.documentElement.scrollTop;
            scrollHeight = document.documentElement.scrollHeight;
            clientHeight = window.innerHeight;
        } else {
            const element = scrollableElement as HTMLElement;
            scrollTop = element.scrollTop;
            scrollHeight = element.scrollHeight;
            clientHeight = element.clientHeight;
        }

        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;
        setShowScrollToBottom(!isNearBottom && scrollHeight > clientHeight);
    }, []);

    useEffect(() => {
        const container = chatContainerRef.current;
        if (!container) return;

        // Find the scrollable parent element
        let scrollableElement: HTMLElement | Window = window;
        let parent: HTMLElement | null = container.parentElement;

        while (parent) {
            const style = window.getComputedStyle(parent);
            if (style.overflowY === "scroll" || style.overflowY === "auto") {
                scrollableElement = parent;
                break;
            }
            parent = parent.parentElement;
        }

        // Initial check
        handleScroll();

        scrollableElement.addEventListener("scroll", handleScroll, { passive: true });
        return () => {
            scrollableElement.removeEventListener("scroll", handleScroll);
        };
    }, [handleScroll, answers, currentStreamingResponse]);

    // Auto-hide scroll button when new messages arrive and user is near bottom
    useEffect(() => {
        if (chatContainerRef.current) {
            const container = chatContainerRef.current;
            const isNearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 100;
            if (isNearBottom) {
                setShowScrollToBottom(false);
            }
        }
    }, [answers, currentStreamingResponse]);

    const onPromptTemplateChange = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setPromptTemplate(newValue || "");
    };

    const onTemperatureChange = (
        newValue: number,
        range?: [number, number],
        event?: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent | React.KeyboardEvent
    ) => {
        setTemperature(newValue);
    };

    const onMinimumSearchScoreChange = (_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setMinimumSearchScore(parseFloat(newValue || "0"));
    };

    const onMinimumRerankerScoreChange = (_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setMinimumRerankerScore(parseFloat(newValue || "0"));
    };

    const onRetrieveCountChange = (_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setRetrieveCount(parseInt(newValue || "3"));
    };

    const onUseSemanticRankerChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSemanticRanker(!!checked);
    };

    const onUseSemanticCaptionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSemanticCaptions(!!checked);
    };

    const onShouldStreamChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setShouldStream(!!checked);
    };

    const onExcludeCategoryChanged = (_ev?: React.FormEvent, newValue?: string) => {
        setExcludeCategory(newValue || "");
    };

    const onUseSuggestFollowupQuestionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSuggestFollowupQuestions(!!checked);
    };

    const onUseOidSecurityFilterChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseOidSecurityFilter(!!checked);
    };

    const onUseGroupsSecurityFilterChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseGroupsSecurityFilter(!!checked);
    };

    const onExampleClicked = (example: string) => {
        if (shouldStream) {
            makeStreamingApiRequest(example, null);
        } else {
            makeApiRequest(example, null);
        }
    };

    const onCoversationStyleChange = (event: any) => {
        setConversationStyle(event.target.value);
        if (event.target.value === "progress") {
            handleProgressReport();
        }
        setIsConfigPanelOpen(false);
    };

    const handleProgressReport = () => {
        setConversationStyle("progress");
        //console.log("handleProgressReport");
        //navigate("/progress");
    };

    const fetchCitation = async (activeCitation: string) => {
        const token = client ? await getToken(client) : undefined;
        if (activeCitation) {
            console.log("");
            // Get hash from the URL as it may contain #page=N
            // which helps browser PDF renderer jump to correct page N
            const originalHash = activeCitation.indexOf("#") ? activeCitation.split("#")[1] : "";
            const response = await fetch(activeCitation, {
                method: "GET",
                headers: getHeaders(token)
            });

            const citationContent = await response.blob();

            const blobURL = window.URL.createObjectURL(citationContent);
            const tempLink = document.createElement("a");
            tempLink.style.display = "none";
            tempLink.href = blobURL;
            tempLink.setAttribute("download", activeCitation);
            // Safari thinks _blank anchor are pop ups. We only want to set _blank
            // target if the browser does not support the HTML5 download attribute.
            // This allows you to download files in desktop safari if pop up blocking
            // is enabled.
            if (typeof tempLink.download === "undefined") {
                tempLink.setAttribute("target", "_blank");
            }
            document.body.appendChild(tempLink);
            tempLink.click();
            document.body.removeChild(tempLink);
            URL.revokeObjectURL(blobURL);
        }
    };

    const onShowCitation = (citation: string, index: number) => {
        //console.log("Clicked citation", citation, activeAnalysisPanelTab, selectedAnswer);
        if (citation.indexOf(".docx") > 0) {
            fetchCitation(citation);
        } else {
            if (activeCitation === citation && activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab && selectedAnswer === index) {
                //console.log("Here 1");
                setActiveAnalysisPanelTab(undefined);
            } else {
                //console.log("Here 2");
                setActiveCitation(citation);
                setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
                //fetchCitation(citation)
            }
        }

        setSelectedAnswer(index);
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }

        setSelectedAnswer(index);
    };

    const toggleThinkingProcess = (index: number) => {
        setExpandedThinking(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    return (
        <div className={styles.container}>
            {/* <div className={styles.commandsContainer}>
                <ClearChatButton className={styles.commandButton} onClick={clearChat} disabled={!lastQuestionRef.current || isLoading} />
                {showUserUpload && <UploadFile className={styles.commandButton} disabled={!isLoggedIn(client)} />}
                <SettingsButton className={styles.commandButton} onClick={() => setIsConfigPanelOpen(!isConfigPanelOpen)} /> */}
            {(showChatHistoryPostgres || showChatHistoryBrowser) && (
                <HistorySidebar
                    provider={historyProvider}
                    isOpen={isHistoryPanelOpen}
                    notify={!isStreaming && !isLoading}
                    onToggle={() => setIsHistoryPanelOpen(!isHistoryPanelOpen)}
                    onNewChat={clearChat}
                    currentChatId={chat_id}
                    onChatSelected={(id, answers, conversationType) => {
                        setChatID(id);
                        if (conversationType) {
                            setConversationStyle(conversationType);
                        }
                        if (answers.length === 0) return;
                        setAnswers(answers);
                        lastQuestionRef.current = answers[answers.length - 1][0];
                        requestAnimationFrame(() => scrollToBottomInstant());
                    }}
                />
            )}
            <div
                className={styles.mainContent}
                style={{ marginLeft: showChatHistoryPostgres || showChatHistoryBrowser ? (isHistoryPanelOpen ? "260px" : "60px") : "0" }}
            >
                <div className={styles.commandsContainer}>
                    <div style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                        <img src={undplogo} alt="UNDP Logo" height={40} style={{ marginRight: "10px" }} />
                        <span style={{ fontWeight: 600, fontSize: "16px", color: "#000" }}>UNDP Serbia</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <ClearChatButton className={styles.commandButton} onClick={clearChat} disabled={!lastQuestionRef.current || isLoading || isStreaming} />
                        {/* {showUserUpload && <UploadFile className={styles.commandButton} disabled={!loggedIn} />} */}
                        <SettingsButton className={styles.commandButton} onClick={() => setIsConfigPanelOpen(!isConfigPanelOpen)} />
                    </div>
                </div>
                <div className={styles.chatRoot}>
                    <div className={styles.commandsSplitContainer}>
                        <div className={styles.commandsContainer}></div>
                    </div>
                    {conversationStyle !== "progress" && (
                        <div className={styles.chatContainer} ref={chatContainerRef}>
                            {!lastQuestionRef.current ? (
                                <div className={styles.chatEmptyState}>
                                    <h1 className={styles.chatEmptyStateTitle}>UNDP Serbia AI Assistant</h1>
                                    <div className={styles.chatEmptyStateSubtitle}>
                                        Ask questions about our analytical work, project results, and development impact or try an example
                                    </div>
                                    <ExampleList onExampleClicked={onExampleClicked} useGPT4V={useGPT4V} />
                                </div>
                            ) : (
                                <div className={styles.chatMessageStream}>
                                    {answers.map((answer, index) => (
                                        <div key={index}>
                                            <UserChatMessage message={answer[0]} />
                                            <div className={styles.chatMessageGpt}>
                                                <img src={logo} alt="Gpt" height="45px" width="45px" />
                                                <div>
                                                    {/* Thinking Process Summary for completed answers */}
                                                    {answerStreamingSteps.has(index) && answerStreamingSteps.get(index)!.length > 0 && (
                                                        <div className={styles.thinkingProcessSummary}>
                                                            <div className={styles.thinkingSummaryButton} onClick={() => toggleThinkingProcess(index)}>
                                                                <span className={styles.thinkingIcon}>🧠</span>
                                                                <span>Thought for {formatElapsedSeconds(totalThinkingTime.get(index), 0)} seconds</span>
                                                                <span className={styles.expandIcon}>{expandedThinking.has(index) ? "▼" : "▶"}</span>
                                                            </div>

                                                            {expandedThinking.has(index) && (
                                                                <div className={styles.thinkingStepsExpanded}>
                                                                    {answerStreamingSteps.get(index)!.map((step, stepIdx) => (
                                                                        <div key={stepIdx} className={`${styles.thinkingStep} ${styles[step.type]}`}>
                                                                            <span className={styles.stepContent}>{step.content}</span>
                                                                            <span className={styles.stepTime}>{formatElapsedMs(step.timestamp)}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    <AnswerBot
                                                        isStreaming={false}
                                                        key={index}
                                                        answer={answer[1]}
                                                        skipTypewriter={skipTypewriterAnswerIndices.has(index)}
                                                        onTypewriterStatusChange={answers.length - 1 === index ? setIsLastAnswerTypewriting : undefined}
                                                        isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                                        onCitationClicked={c => onShowCitation(c, index)}
                                                        onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                        onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                        onFollowupQuestionClicked={q => {
                                                            console.log("Follow-up question clicked:", q);
                                                            if (shouldStream) {
                                                                makeStreamingApiRequest(q, null);
                                                            } else {
                                                                makeApiRequest(q, null);
                                                            }
                                                        }}
                                                        showFollowupQuestions={answers.length - 1 === index}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {isStreaming && (
                                        <>
                                            <UserChatMessage message={lastQuestionRef.current} />
                                            <div className={styles.chatMessageGpt}>
                                                <img src={logo} alt="Gpt" height="45px" width="45px" />
                                                <div className={styles.streamingContainer}>
                                                    {/* Current Thinking Step - ChatGPT style */}
                                                    {streamingStatus && (
                                                        <div className={styles.currentThinkingStep}>
                                                            <div className={styles.thinkingIndicator}>
                                                                <span className={styles.thinkingIcon}>🧠</span>
                                                                <span className={styles.currentStepText}>{streamingStatus}</span>
                                                                <div className={styles.thinkingSpinner}></div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Streaming Content */}
                                                    {currentStreamingResponse && (
                                                        <AnswerBot
                                                            isStreaming={true}
                                                            answer={{
                                                                message: currentStreamingResponse,
                                                                session_state: chat_id,
                                                                followup_questions: []
                                                            }}
                                                            isSelected={false}
                                                            onCitationClicked={() => {}}
                                                            onThoughtProcessClicked={() => {}}
                                                            onSupportingContentClicked={() => {}}
                                                            onFollowupQuestionClicked={() => {}}
                                                            showFollowupQuestions={false}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    {isLoading && !isStreaming && (
                                        <>
                                            <UserChatMessage message={lastQuestionRef.current} />
                                            <div className={styles.chatMessageGptMinWidth}>
                                                <img src={logo} alt="Gpt" height="45px" width="45px" />
                                                <AnswerLoading />
                                            </div>
                                        </>
                                    )}
                                    {error ? (
                                        <>
                                            <UserChatMessage message={lastQuestionRef.current} />
                                            <div className={styles.chatMessageGptMinWidth}>
                                                <img src={logo} alt="Gpt" height="45px" width="45px" />
                                                <AnswerError
                                                    error={error.toString()}
                                                    onRetry={() => {
                                                        if (shouldStream) {
                                                            makeStreamingApiRequest(lastQuestionRef.current, null);
                                                        } else {
                                                            makeApiRequest(lastQuestionRef.current, null);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </>
                                    ) : null}
                                    <div ref={chatMessageStreamEnd} />
                                </div>
                            )}

                            {/* Scroll to bottom button */}
                            {showScrollToBottom && (
                                <div className={styles.scrollToBottomContainer}>
                                    <button className={styles.scrollToBottomButton} onClick={scrollToBottom} title="Scroll to bottom">
                                        <ChevronDown20Filled />
                                        <span>Scroll to bottom</span>
                                    </button>
                                </div>
                            )}

                            {conversationStyle !== "creative" && (
                                <div className={styles.modeTag}>
                                    <span className={styles.modeTagText}>
                                        Current Mode:{" "}
                                        {conversationStyle === "analytics"
                                            ? "Analytics"
                                            : conversationStyle === "balanced"
                                            ? "Deep Dive Results"
                                            : conversationStyle === "progress"
                                            ? "Project Progress Report"
                                            : conversationStyle}
                                    </span>
                                    <button
                                        className={styles.modeTagClose}
                                        onClick={() => {
                                            clearChat();
                                            setConversationStyle("creative");
                                        }}
                                        title="Clear chat and switch to default mode"
                                        aria-label="Remove filter and clear chat"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            )}
                            <div className={styles.chatInput}>
                                <QuestionInput
                                    clearOnSend
                                    placeholder="Ask anything..."
                                    disabled={isLoading || isStreaming}
                                    disableInput={isLoading || (isStreaming && !isStreamingOutputSuppressed)}
                                    showStop={(isStreaming && !isStreamingOutputSuppressed) || isLastAnswerTypewriting}
                                    onStop={handleStop}
                                    onSend={(question, file) => {
                                        if (shouldStream) {
                                            makeStreamingApiRequest(question, file);
                                        } else {
                                            makeApiRequest(question, file);
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {activeAnalysisPanelTab && conversationStyle !== "progress" && (
                        <AnalysisPanel
                            className={styles.chatAnalysisPanel}
                            activeCitation={activeCitation}
                            onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                            citationHeight="810px"
                            activeTab={activeAnalysisPanelTab}
                        />
                    )}

                    {conversationStyle === "progress" && <Progress />}

                    <Panel
                        headerText="Dive Deeper Into Reports, Data or Results"
                        isOpen={isConfigPanelOpen}
                        isBlocking={false}
                        onDismiss={() => setIsConfigPanelOpen(false)}
                        closeButtonAriaLabel="Close"
                        onRenderFooterContent={() => <DefaultButton onClick={() => setIsConfigPanelOpen(false)}>Close</DefaultButton>}
                        isFooterAtBottom={true}
                    >
                        {lastQuestionRef.current != "" && (
                            <MessageBar
                                messageBarType={MessageBarType.info}
                                styles={{
                                    root: { marginTop: 16, marginBottom: 16 }
                                }}
                            >
                                Mode selection is disabled while a conversation is active. Clear the chat to change the conversation mode.
                            </MessageBar>
                        )}
                        <Field style={{ marginTop: 16 }} className={lastQuestionRef.current == "" ? "" : styles.disabledField}>
                            <RadioGroup onChange={onCoversationStyleChange} defaultValue={conversationStyle} disabled={lastQuestionRef.current != ""}>
                                {/* <Radio
                                value="creative"
                                label={
                                    <>
                                        <Brush size={16} style={{ marginRight: 8 }} />
                                        Main{" "}
                                        <p className={styles.radioDescription}>
                                            Uses semantic databases and is designed to be more creative in text generation. It likely leverages natural language
                                            understanding to provide more contextually rich and nuanced responses. This type of conversation is ideal for tasks
                                            that require a more conversational and flexible approach, such as talking points, content creation, or personal
                                            assistance.
                                        </p>
                                    </>
                                }
                                className={`${styles.customRadio} ${conversationStyle === "creative" ? styles.selected : ""}`}
                            /> */}
                                <Radio
                                    value="analytics"
                                    label={
                                        <>
                                            <Scale size={16} style={{ marginRight: 8 }} />
                                            Analytics{" "}
                                            <p className={styles.radioDescription}>
                                                Deep dive into UNDP Serbia's analytical reports database. Search for specific topics and get detailed insights
                                                from the internal and set of relevant external reports.
                                            </p>
                                        </>
                                    }
                                    className={`${styles.customRadio} ${conversationStyle === "analytics" ? styles.selected : ""}`}
                                />
                                <Radio
                                    value="balanced"
                                    label={
                                        <>
                                            <Scale size={16} style={{ marginRight: 8 }} />
                                            Deep Dive Results{" "}
                                            <p className={styles.radioDescription}>
                                                Designed for accuracy and consistency, making it suitable for tasks where precision and factual correctness are
                                                paramount, such as: as generating reports, providing factual answers, or handling tasks where specific data
                                                retrieval is required.
                                            </p>
                                        </>
                                    }
                                    className={`${styles.customRadio} ${conversationStyle === "balanced" ? styles.selected : ""}`}
                                />
                                <Radio
                                    value="progress"
                                    label={
                                        <>
                                            <FileText size={16} style={{ marginRight: 8 }} />
                                            Project Progress Report <p className={styles.radioDescription}>Auto generate project progress report document.</p>
                                        </>
                                    }
                                    className={`${styles.customRadio} ${conversationStyle === "progress" ? styles.selected : ""}`}
                                />
                            </RadioGroup>
                        </Field>
                    </Panel>
                </div>
            </div>
        </div>
    );
};

export default Chat;
